import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './Users.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * Users - View and manage all users on the platform
 * Uses existing backend endpoint: GET /admin-user/:id
 */
function Users() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all'); // all, user, admin, team
    const [statusFilter, setStatusFilter] = useState('all'); // all, trial, active, expired
    const [periodFilter, setPeriodFilter] = useState('all'); // all, today, yesterday, thisMonth, lastMonth

    // Pagination state (from backend)
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    // New-signup counts by period (from backend).
    const [signupStats, setSignupStats] = useState({ today: 0, yesterday: 0, thisMonth: 0, lastMonth: 0 });

    // Debounce search
    const [searchDebounce, setSearchDebounce] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchTerm);
            setCurrentPage(1); // Reset to page 1 on search
        }, 500); // 500ms debounce

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchUsers();
    }, [currentPage, itemsPerPage, searchDebounce, roleFilter, statusFilter, periodFilter]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            const params = new URLSearchParams({
                page: currentPage,
                limit: itemsPerPage,
                search: searchDebounce,
                roleFilter: roleFilter,
                statusFilter: statusFilter,
                period: periodFilter
            });

            const response = await axios.get(
                `${BASEURL}admin-user/${userId}?${params}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setUsers(response.data.data.users);
                setTotalPages(response.data.data.pagination.totalPages);
                setTotalUsers(response.data.data.pagination.totalUsers);
                if (response.data.data.signupStats) setSignupStats(response.data.data.signupStats);
            } else {
                setError(response.data.error || 'Failed to load users');
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users');
            setLoading(false);
        }
    };

    const handleDeleteUser = async (user) => {
        if (!window.confirm(`Are you sure you want to delete user: ${user.username || user.mobile}?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');
            const response = await axios.post(
                `${BASEURL}admin-user-delete/${userId}`,
                { user: user._id },
                { headers: { token } }
            );

            alert('User deleted successfully');
            fetchUsers(); // Refresh current page
        } catch (err) {
            console.error('Error deleting user:', err);
            alert('Failed to delete user');
        }
    };

    // Edit user state and handlers
    const [editingUser, setEditingUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({
        username: '',
        email: '',
        mobile: '',
        role: 0,
        package: 1000,
        smsBalance: 0,
        IsTrial: false,
        IsPaid: false,
        subscriptionStatus: 'suspended',
        userStatus: 0
    });

    const openEditModal = (user) => {
        setEditingUser(user);
        setEditFormData({
            username: user.username || '',
            email: user.email || '',
            mobile: user.mobile || '',
            role: user.role || 0,
            package: user.package || 1000,
            smsBalance: user.smsBalance || 0,
            IsTrial: user.IsTrial !== undefined ? user.IsTrial : false,
            IsPaid: user.IsPaid !== undefined ? user.IsPaid : false,
            subscriptionStatus: user.subscriptionStatus || 'suspended',
            userStatus: user.userStatus || 0
        });
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingUser(null);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');
            const response = await axios.post(
                `${BASEURL}admin-user-update/${userId}`,
                {
                    user: editingUser._id,
                    ...editFormData
                },
                { headers: { token } }
            );

            if (response.data.success) {
                alert('User updated successfully');
                closeEditModal();
                fetchUsers();
            } else {
                alert(response.data.error || 'Failed to update user');
            }
        } catch (err) {
            console.error('Error updating user:', err);
            alert(err.response?.data?.error || 'Failed to update user');
        }
    };

    // Create user state and handlers
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        username: '',
        email: '',
        mobile: '',
        password: '',
        role: 0,
        package: 1000,
        smsBalance: 0,
        IsTrial: false,
        IsPaid: false,
        subscriptionStatus: 'suspended',
        userStatus: 0
    });

    const openCreateModal = () => {
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setCreateFormData({
            username: '',
            email: '',
            mobile: '',
            password: '',
            role: 0,
            package: 1000,
            smsBalance: 0,
            IsTrial: true,
            IsPaid: false,
            subscriptionStatus: 'trial',
            userStatus: 0
        });
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.mobile) {
            alert('Mobile number is required');
            return;
        }

        if (!createFormData.password) {
            alert('Password is required');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');
            const response = await axios.post(
                `${BASEURL}admin-user-create/${userId}`,
                createFormData,
                { headers: { token } }
            );

            if (response.data.success) {
                alert('User created successfully');
                closeCreateModal();
                fetchUsers();
            } else {
                alert(response.data.error || 'Failed to create user');
            }
        } catch (err) {
            console.error('Error creating user:', err);
            alert(err.response?.data?.error || 'Failed to create user');
        }
    };

    const getRoleBadge = (role) => {
        const roles = {
            0: { label: 'User', class: styles.roleUser },
            1: { label: 'Super', class: styles.roleAdmin },
            2: { label: 'Team', class: styles.roleTeam }
        };
        const roleData = roles[role] || { label: 'Unknown', class: '' };
        return <span className={`${styles.roleBadge} ${roleData.class}`}>{roleData.label}</span>;
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            trial: styles.statusTrial,
            active: styles.statusActive,
            expired: styles.statusExpired,
            suspended: styles.statusSuspended,
            pending_payment: styles.statusPending
        };

        return (
            <span className={`${styles.statusBadge} ${statusClasses[status] || ''}`}>
                {status || 'none'}
            </span>
        );
    };

    if (loading) {
        return (
            <Layout>
                <div className={styles.loading}>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading users...
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className={styles.error}>
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                </div>
            </Layout>
        );
    }

    // Pagination helpers
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalUsers);

    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const getPaginationRange = () => {
        const range = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            range.push(i);
        }
        return range;
    };

    // Click a stat card to filter the list by signup period (and reset to page 1).
    const selectPeriod = (p) => {
        setPeriodFilter((prev) => (prev === p ? 'all' : p)); // click active card again = clear
        setCurrentPage(1);
    };
    const periodLabels = { today: 'Today', yesterday: 'Yesterday', thisMonth: 'This Month', lastMonth: 'Last Month' };

    return (
        <Layout>
            <div className={styles.container}>
                {/* ===== Hero header + stats (green theme, matches /billing) ===== */}
                <div className={styles.pageHero}>
                    <div className={styles.pageHeroTop}>
                        <div>
                            <h1 className={styles.pageTitle}>User Management</h1>
                            <p className={styles.pageSubtitle}>View and manage all users on the platform</p>
                        </div>
                        <button className={styles.addBtn} onClick={openCreateModal}>
                            <i className="fas fa-plus"></i> Add User
                        </button>
                    </div>

                    <div className={styles.statRow}>
                        <div
                            className={`${styles.statCard} ${styles.statCardHero} ${periodFilter === 'all' ? styles.statCardActive : ''}`}
                            onClick={() => selectPeriod('all')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectPeriod('all'); }}
                            title="Show all users"
                        >
                            <span className={styles.statNum}>{Number(totalUsers).toLocaleString()}</span>
                            <span className={styles.statLabel}>Total Users</span>
                        </div>
                        {[
                            { label: 'Today', value: signupStats.today, period: 'today' },
                            { label: 'Yesterday', value: signupStats.yesterday, period: 'yesterday' },
                            { label: 'This Month', value: signupStats.thisMonth, period: 'thisMonth' },
                            { label: 'Last Month', value: signupStats.lastMonth, period: 'lastMonth' },
                        ].map((s) => (
                            <div
                                key={s.label}
                                className={`${styles.statCard} ${periodFilter === s.period ? styles.statCardActive : ''}`}
                                onClick={() => selectPeriod(s.period)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectPeriod(s.period); }}
                                title={`Show ${s.label} signups`}
                            >
                                <span className={styles.statTag}>NEW</span>
                                <span className={styles.statNum}>{s.value}</span>
                                <span className={styles.statLabel}>{s.label}</span>
                            </div>
                        ))}
                    </div>
                    <div className={styles.pageMeta}>
                        {periodFilter === 'all' ? (
                            <><i className="fas fa-hand-pointer"></i> Click a card to filter by signup period</>
                        ) : (
                            <>
                                <i className="fas fa-filter"></i> Showing <strong>{periodLabels[periodFilter]}</strong> signups ({totalUsers})
                                <button type="button" className={styles.clearFilterBtn} onClick={() => selectPeriod('all')}>✕ Clear</button>
                            </>
                        )}
                    </div>
                </div>

                {/* Filters */}
                <div className={styles.filtersSection}>
                    <div className={styles.filtersRow}>
                        <div className={styles.searchBox}>
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search by name, mobile, email, or store name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className={styles.filterGroup}>
                            <label>Role:</label>
                            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                                <option value="all">All</option>
                                <option value="user">Users</option>
                                <option value="admin">Super Admins</option>
                                <option value="team">Team Members</option>
                            </select>
                        </div>

                        <div className={styles.filterGroup}>
                            <label>Status:</label>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">All</option>
                                <option value="active">Active (paying)</option>
                                <option value="expired">Expired (lapsed)</option>
                                <option value="suspended">Suspended (unpaid)</option>
                                <option value="trial">Trial (legacy)</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className={styles.tableContainer}>
                    <table className={styles.usersTable}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Mobile</th>
                                <th>Store Name</th>
                                <th>Role</th>
                                <th>Package</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className={styles.noData}>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        <div>Loading...</div>
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className={styles.noData}>
                                        <i className="fas fa-inbox"></i>
                                        <div>No users found</div>
                                        <small style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px', display: 'block' }}>
                                            Try adjusting your filters or search term
                                        </small>
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <tr key={user._id}>
                                        <td>
                                            <strong>{user.username || 'N/A'}</strong>
                                        </td>
                                        <td>{user.mobile || 'N/A'}</td>
                                        <td title={user.StoreName || user.storeName || 'No store'}>
                                            {((user.StoreName || user.storeName || 'No store').length > 10
                                                ? (user.StoreName || user.storeName || 'No store').substring(0, 10) + '...'
                                                : (user.StoreName || user.storeName || 'No store'))}
                                        </td>
                                        <td>{getRoleBadge(user.role)}</td>
                                        <td>
                                            <span className={styles.packageBadge}>
                                                ৳{user.package || 0}
                                            </span>
                                        </td>
                                        <td>
                                            {(() => {
                                                const s = user.displayStatus
                                                    || (user.IsTrial ? 'trial' : user.IsPaid ? 'active' : 'suspended');
                                                const map = {
                                                    active: { cls: styles.statusActive, label: 'ACTIVE' },
                                                    expired: { cls: styles.statusExpired, label: 'EXPIRED' },
                                                    trial: { cls: styles.statusTrial, label: 'TRIAL' },
                                                    offer: { cls: styles.statusPending || styles.statusTrial, label: 'OFFER' },
                                                    suspended: { cls: styles.statusSuspended || styles.statusExpired, label: 'SUSPENDED' },
                                                };
                                                const m = map[s] || { cls: styles.statusExpired, label: String(s).toUpperCase() };
                                                return <span className={`${styles.statusBadge} ${m.cls || ''}`}>{m.label}</span>;
                                            })()}
                                        </td>
                                        <td>
                                            {user.Date ? new Date(user.Date).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.viewBtn}
                                                    onClick={() => alert(`View user: ${user.username}\nMobile: ${user.mobile}\nID: ${user._id}`)}
                                                    title="View details"
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </button>
                                                <button
                                                    className={styles.editBtn}
                                                    onClick={() => openEditModal(user)}
                                                    title="Edit user"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                {user.role !== 1 && (
                                                    <button
                                                        className={styles.deleteBtn}
                                                        onClick={() => handleDeleteUser(user)}
                                                        title="Delete user"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className={styles.pagination}>
                    <div className={styles.paginationInfo}>
                        <span>
                            Showing {startIndex + 1}-{endIndex} of {totalUsers} users
                            {searchDebounce && ` (filtered)`}
                        </span>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className={styles.itemsPerPageSelect}
                        >
                            <option value={10}>10 per page</option>
                            <option value={20}>20 per page</option>
                            <option value={50}>50 per page</option>
                            <option value={100}>100 per page</option>
                        </select>
                    </div>

                    {totalPages > 1 && (
                        <div className={styles.paginationControls}>
                            <button
                                onClick={() => goToPage(1)}
                                disabled={currentPage === 1}
                                className={styles.pageBtn}
                                title="First page"
                            >
                                &laquo;
                            </button>
                            <button
                                onClick={() => goToPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={styles.pageBtn}
                                title="Previous page"
                            >
                                &lsaquo;
                            </button>

                            {getPaginationRange().map(page => (
                                <button
                                    key={page}
                                    onClick={() => goToPage(page)}
                                    className={`${styles.pageBtn} ${currentPage === page ? styles.activePage : ''}`}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                onClick={() => goToPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={styles.pageBtn}
                                title="Next page"
                            >
                                &rsaquo;
                            </button>
                            <button
                                onClick={() => goToPage(totalPages)}
                                disabled={currentPage === totalPages}
                                className={styles.pageBtn}
                                title="Last page"
                            >
                                &raquo;
                            </button>
                        </div>
                    )}
                </div>

                {/* Edit User Modal */}
                {showEditModal && (
                    <div className={styles.modalOverlay} onClick={closeEditModal}>
                        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <h2>Edit User</h2>
                                <button className={styles.closeBtn} onClick={closeEditModal}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <form onSubmit={handleEditSubmit} className={styles.modalForm}>
                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label>Username</label>
                                        <input
                                            type="text"
                                            value={editFormData.username}
                                            onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                                            placeholder="Enter username"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            value={editFormData.email}
                                            onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                            placeholder="Enter email"
                                        />
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label>Mobile *</label>
                                        <input
                                            type="text"
                                            value={editFormData.mobile}
                                            onChange={(e) => setEditFormData({ ...editFormData, mobile: e.target.value })}
                                            placeholder="Enter mobile number"
                                            disabled
                                            title="Mobile number cannot be changed"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Role</label>
                                        <select
                                            value={editFormData.role}
                                            onChange={(e) => setEditFormData({ ...editFormData, role: parseInt(e.target.value) })}
                                        >
                                            <option value={0}>User</option>
                                            <option value={1}>Super Admin</option>
                                            <option value={2}>Team</option>
                                        </select>
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label>Package (৳)</label>
                                        <input
                                            type="number"
                                            value={editFormData.package}
                                            onChange={(e) => setEditFormData({ ...editFormData, package: parseInt(e.target.value) })}
                                            placeholder="Enter package amount"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>SMS Balance</label>
                                        <input
                                            type="number"
                                            value={editFormData.smsBalance}
                                            onChange={(e) => setEditFormData({ ...editFormData, smsBalance: parseInt(e.target.value) })}
                                            placeholder="Enter SMS balance"
                                        />
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label>Subscription Status</label>
                                        <select
                                            value={editFormData.subscriptionStatus}
                                            onChange={(e) => setEditFormData({ ...editFormData, subscriptionStatus: e.target.value })}
                                        >
                                            <option value="trial">Trial</option>
                                            <option value="active">Active</option>
                                            <option value="pending_payment">Pending Payment</option>
                                            <option value="expired">Expired</option>
                                            <option value="suspended">Suspended</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>User Status</label>
                                        <select
                                            value={editFormData.userStatus}
                                            onChange={(e) => setEditFormData({ ...editFormData, userStatus: parseInt(e.target.value) })}
                                        >
                                            <option value={0}>Active</option>
                                            <option value={1}>Suspended</option>
                                        </select>
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={editFormData.IsTrial}
                                                onChange={(e) => setEditFormData({ ...editFormData, IsTrial: e.target.checked })}
                                            />
                                            <span>Is Trial</span>
                                        </label>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={editFormData.IsPaid}
                                                onChange={(e) => setEditFormData({ ...editFormData, IsPaid: e.target.checked })}
                                            />
                                            <span>Is Paid</span>
                                        </label>
                                    </div>
                                </div>

                                <div className={styles.modalActions}>
                                    <button type="button" className={styles.cancelBtn} onClick={closeEditModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className={styles.submitBtn}>
                                        <i className="fas fa-save"></i>
                                        Update User
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Create User Modal */}
                {showCreateModal && (
                    <div className={styles.modalOverlay} onClick={closeCreateModal}>
                        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <h2>Create New User</h2>
                                <button className={styles.closeBtn} onClick={closeCreateModal}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <form onSubmit={handleCreateSubmit} className={styles.modalForm}>
                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label>Username</label>
                                        <input
                                            type="text"
                                            value={createFormData.username}
                                            onChange={(e) => setCreateFormData({ ...createFormData, username: e.target.value })}
                                            placeholder="Enter username"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            value={createFormData.email}
                                            onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                                            placeholder="Enter email"
                                        />
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label>Mobile * (Required)</label>
                                        <input
                                            type="text"
                                            value={createFormData.mobile}
                                            onChange={(e) => setCreateFormData({ ...createFormData, mobile: e.target.value })}
                                            placeholder="Enter mobile number"
                                            required
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Password * (Required)</label>
                                        <input
                                            type="password"
                                            value={createFormData.password}
                                            onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                                            placeholder="Enter password"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label>Role</label>
                                        <select
                                            value={createFormData.role}
                                            onChange={(e) => setCreateFormData({ ...createFormData, role: parseInt(e.target.value) })}
                                        >
                                            <option value={0}>User</option>
                                            <option value={1}>Super Admin</option>
                                            <option value={2}>Team</option>
                                        </select>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Package (৳)</label>
                                        <input
                                            type="number"
                                            value={createFormData.package}
                                            onChange={(e) => setCreateFormData({ ...createFormData, package: parseInt(e.target.value) })}
                                            placeholder="Enter package amount"
                                        />
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label>SMS Balance</label>
                                        <input
                                            type="number"
                                            value={createFormData.smsBalance}
                                            onChange={(e) => setCreateFormData({ ...createFormData, smsBalance: parseInt(e.target.value) })}
                                            placeholder="Enter SMS balance"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Subscription Status</label>
                                        <select
                                            value={createFormData.subscriptionStatus}
                                            onChange={(e) => setCreateFormData({ ...createFormData, subscriptionStatus: e.target.value })}
                                        >
                                            <option value="trial">Trial</option>
                                            <option value="active">Active</option>
                                            <option value="pending_payment">Pending Payment</option>
                                            <option value="expired">Expired</option>
                                            <option value="suspended">Suspended</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={createFormData.IsTrial}
                                                onChange={(e) => setCreateFormData({ ...createFormData, IsTrial: e.target.checked })}
                                            />
                                            <span>Is Trial</span>
                                        </label>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={createFormData.IsPaid}
                                                onChange={(e) => setCreateFormData({ ...createFormData, IsPaid: e.target.checked })}
                                            />
                                            <span>Is Paid</span>
                                        </label>
                                    </div>
                                </div>

                                <div className={styles.modalActions}>
                                    <button type="button" className={styles.cancelBtn} onClick={closeCreateModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className={styles.submitBtn}>
                                        <i className="fas fa-plus"></i>
                                        Create User
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default Users;
